/**
 * SME Climate Journey — Redesigned for simplicity
 *
 * 1 screen = 1 purpose = 1 next action
 *
 * 5 steps:
 *   1. About Your Business
 *   2. Enter Your Data
 *   3. See Your Impact
 *   4. Your Opportunities
 *   5. Get Financing
 *
 * Design principles:
 *   - No tabs, no warnings, no technical language
 *   - Sequential: each step unlocks the next
 *   - Completed steps show a collapsed summary with green check
 *   - One CTA visible at a time
 *   - Replace "confidence 0.78" with "Your data looks good"
 *   - Replace "Scope 1" with "Direct fuel emissions"
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import client from "@/api/client";
import { toast } from "sonner";
import {
  CheckCircle2, ChevronDown, ChevronRight, RefreshCw,
  ArrowRight, Building2, BarChart2, Target, DollarSign,
  Zap, X, Check
} from "lucide-react";
import { useJourney } from "@/context/JourneyContext";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
// ── Colour / style constants ──────────────────────────────────────────────
const BRAND = "#0F172A";
const TEAL  = "#059669";

// ── Progress Header ────────────────────────────────────────────────────────
const STEP_DEFS = [
  { id: "profile",      label: "Your Business",  icon: Building2  },
  { id: "data",         label: "Enter Data",      icon: BarChart2  },
  { id: "impact",       label: "Your Impact",     icon: BarChart2  },
  { id: "opportunities",label: "Opportunities",   icon: Target     },
  { id: "financing",    label: "Financing",       icon: DollarSign },
];

function ProgressHeader({ currentStep, completedSteps }) {
  return (
    <div className="bg-white border-b border-slate-100 px-6 py-4 sticky top-0 z-20" data-testid="progress-header">
      <div className="flex items-center gap-0">
        {STEP_DEFS.map((s, i) => {
          const done    = completedSteps.includes(s.id);
          const active  = currentStep === s.id;
          const locked  = !done && !active;
          return (
            <React.Fragment key={s.id}>
              {i > 0 && (
                <div className={`flex-1 h-0.5 ${done || active ? "bg-teal-500" : "bg-slate-200"}`} />
              )}
              <div className="flex flex-col items-center flex-shrink-0">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center border-2 transition-all ${
                  done   ? "bg-teal-500 border-teal-500" :
                  active ? "bg-white border-teal-500" :
                           "bg-white border-slate-200"
                }`}>
                  {done
                    ? <Check size={13} className="text-white" strokeWidth={3} />
                    : <span className={`text-[11px] font-bold ${active ? "text-teal-600" : "text-slate-300"}`}>{i+1}</span>
                  }
                </div>
                <p className={`text-xs mt-0.5 font-semibold hidden sm:block ${
                  done ? "text-teal-600" : active ? "text-slate-900" : "text-slate-300"
                }`}>{s.label}</p>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// ── Step Card wrapper ──────────────────────────────────────────────────────
function StepCard({ id, number, title, icon: Icon, status, summary, isExpanded, onExpand, headerAction, children }) {
  const isActive    = status === "active";
  const isCompleted = status === "completed";
  const isLocked    = status === "locked";
  const showContent = isActive || (isCompleted && isExpanded);

  return (
    <div className={`bg-white rounded-xl border-2 overflow-hidden transition-all ${
      isActive    ? "border-teal-400 shadow-sm shadow-teal-100" :
      isCompleted ? "border-emerald-200" :
                    "border-slate-100 opacity-50"
    }`} data-testid={`step-${id}`}>
      {/* Header */}
      <div className={`flex items-center gap-3 px-5 py-4 ${isCompleted ? "cursor-pointer hover:bg-slate-50" : ""}`}
        onClick={isCompleted ? onExpand : undefined}>
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
          isCompleted ? "bg-emerald-100" : isActive ? "bg-teal-50" : "bg-slate-100"
        }`}>
          {isCompleted
            ? <CheckCircle2 size={18} className="text-emerald-500" />
            : <Icon size={18} className={isActive ? "text-teal-600" : "text-slate-300"} />
          }
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-bold uppercase tracking-widest ${
            isActive ? "text-teal-500" : isCompleted ? "text-emerald-500" : "text-slate-300"
          }`}>Step {number}</p>
          <p className={`text-sm font-bold ${isLocked ? "text-slate-300" : "text-slate-900"}`}>{title}</p>
          {isCompleted && !isExpanded && summary && (
            <p className="text-xs text-slate-400 mt-0.5 truncate">{summary}</p>
          )}
        </div>
        {/* Per-step quick action (e.g. Edit Data for Step 2) */}
        {isCompleted && headerAction && (
          <div onClick={e => e.stopPropagation()} className="flex-shrink-0">
            {headerAction}
          </div>
        )}
        {isCompleted && (isExpanded
          ? <ChevronDown size={14} className="text-slate-400 flex-shrink-0 rotate-180" />
          : <ChevronDown size={14} className="text-slate-400 flex-shrink-0" />)}
        {isLocked && (
          <span className="text-xs text-slate-300 flex-shrink-0">Complete previous step</span>
        )}
      </div>
      {/* Content */}
      {showContent && (
        <div className="border-t border-slate-100 px-5 pb-5 pt-4">
          {children}
        </div>
      )}
    </div>
  );
}

// ── Step 1: About Your Business ───────────────────────────────────────────
function StepProfile({ org }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-4">
        {[
          ["Company",  org?.name     || "—"],
          ["Sector",   org?.sector   || "—"],
          ["Country",  org?.country  || "—"],
          ["Team",     org?.employee_count ? `${org.employee_count} people` : "—"],
        ].map(([l, v]) => (
          <div key={l} className="bg-slate-50 rounded-xl px-4 py-3">
            <p className="text-xs text-slate-400">{l}</p>
            <p className="text-sm font-semibold text-slate-900">{v}</p>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-400 italic">{org?.description || ""}</p>
    </div>
  );
}

// ── Step 2: Enter Your Data ────────────────────────────────────────────────
// Reuses logic from our EmissionsInputForm but with much cleaner copy
function StepEnterData({ assessmentId, onCalculated, initialValues }) {
  const [period,   setPeriod]   = useState("annual");
  const [form,     setForm]     = useState({
    fleet_diesel: initialValues?.fleet_diesel_liters  ? String(initialValues.fleet_diesel_liters)  : "",
    gen_diesel:   initialValues?.generator_diesel_liters ? String(initialValues.generator_diesel_liters) : "",
    electricity:  initialValues?.electricity_kwh      ? String(initialValues.electricity_kwh)      : "",
    gas:          initialValues?.gas_throughput_tonnes ? String(initialValues.gas_throughput_tonnes) : "",
  });
  const [preview,  setPreview]  = useState(null);
  const [busy,     setBusy]     = useState(false);
  const isEdit = !!(initialValues?.fleet_diesel_liters || initialValues?.generator_diesel_liters || initialValues?.electricity_kwh);

  // Sync form when initialValues change (e.g. after first load)
  useEffect(() => {
    if (initialValues) {
      setForm({
        fleet_diesel: initialValues.fleet_diesel_liters   ? String(initialValues.fleet_diesel_liters)   : "",
        gen_diesel:   initialValues.generator_diesel_liters ? String(initialValues.generator_diesel_liters) : "",
        electricity:  initialValues.electricity_kwh       ? String(initialValues.electricity_kwh)       : "",
        gas:          initialValues.gas_throughput_tonnes ? String(initialValues.gas_throughput_tonnes)  : "",
      });
    }
  }, [
    initialValues?.fleet_diesel_liters,
    initialValues?.generator_diesel_liters,
    initialValues?.electricity_kwh,
  ]);

  useEffect(() => {
    const EF_D = 2.68, mult = period === "monthly" ? 12 : 1;
    const EF_E = 0.43;
    const f = (parseFloat(form.fleet_diesel) || 0) * mult * EF_D;
    const g = (parseFloat(form.gen_diesel)   || 0) * mult * EF_D;
    const e = (parseFloat(form.electricity)  || 0) * mult * EF_E;
    const t = (f + g + e) / 1000;
    setPreview(t > 0 ? Math.round(t * 10) / 10 : null);
  }, [form, period]);

  const handleCalculate = async () => {
    if (!preview) { toast.warning("Enter at least one value to continue."); return; }
    setBusy(true);
    try {
      const mult = period === "monthly" ? 12 : 1;
      const payload = {
        assessment_id:          assessmentId,
        fleet_diesel_liters:    form.fleet_diesel ? parseFloat(form.fleet_diesel) * mult : null,
        generator_diesel_liters:form.gen_diesel   ? parseFloat(form.gen_diesel)   * mult : null,
        electricity_kwh:        form.electricity  ? parseFloat(form.electricity)  * mult : null,
        gas_throughput_tonnes:  form.gas          ? parseFloat(form.gas)          * mult : null,
        grid_factor_source:     "nigeria_default",
      };
      const res = await client.post("/sme/submit-emissions", payload);
      toast.success(isEdit ? "Data updated. Recalculating your impact..." : "Data saved. Your impact has been calculated.");
      onCalculated(res.data);
    } catch (err) {
      toast.error("Something went wrong. Please check your numbers and try again.");
    }
    setBusy(false);
  };

  const inputs = [
    { key: "fleet_diesel",  label: "Fuel — fleet vehicles",    unit: "liters",  placeholder: "e.g. 7200", hint: "Diesel used by your trucks and cars" },
    { key: "gen_diesel",    label: "Fuel — generators",        unit: "liters",  placeholder: "e.g. 2800", hint: "Diesel for depot and office generators" },
    { key: "electricity",   label: "Electricity",              unit: "kWh",     placeholder: "e.g. 145000", hint: "From your electricity bills" },
    { key: "gas",           label: "Gas handled (optional)",   unit: "tonnes",  placeholder: "e.g. 500", hint: "LPG/CNG you distribute — not added to your footprint" },
  ];

  return (
    <div className="space-y-4">
      {/* Period toggle */}
      <div className="flex items-center gap-3">
        <p className="text-xs text-slate-500">I'm entering:</p>
        <div className="flex rounded-xl border border-slate-200 overflow-hidden">
          {[["annual","Annual total"], ["monthly","Monthly (×12)"]].map(([v, l]) => (
            <button key={v} onClick={() => setPeriod(v)}
              className={`px-3 py-1.5 text-xs font-medium ${period === v ? "bg-teal-600 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}
              data-testid={`period-${v}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* Inputs */}
      <div className="space-y-2">
        {inputs.map(f => (
          <div key={f.key} className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3">
            <div className="flex-1">
              <label className="text-xs font-semibold text-slate-900">{f.label}</label>
              <p className="text-xs text-slate-400">{f.hint}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <input
                type="number" min="0"
                value={form[f.key]}
                onChange={e => setForm(p => ({...p, [f.key]: e.target.value}))}
                placeholder={f.placeholder}
                className="w-24 h-9 px-3 border border-slate-200 rounded-xl text-sm text-right focus:outline-none focus:border-teal-400 bg-white"
                data-testid={`input-${f.key}`}
              />
              <span className="text-xs text-slate-400 w-12">{f.unit}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Live preview */}
      {preview !== null && (
        <div className="bg-teal-50 border border-teal-200 rounded-xl px-5 py-4 flex items-center gap-4"
          data-testid="live-preview">
          <div>
            <p className="text-xs text-teal-500 font-semibold uppercase tracking-wide">Your estimated footprint</p>
            <p className="text-3xl font-bold text-teal-700">{preview} <span className="text-base font-normal">tCO₂e/yr</span></p>
          </div>
          <p className="text-xs text-teal-400 ml-auto">Updates as you type</p>
        </div>
      )}

      <button onClick={handleCalculate} disabled={busy || !preview}
        className="w-full h-12 bg-teal-600 text-white text-sm font-bold rounded-xl hover:bg-teal-700 disabled:opacity-40 flex items-center justify-center gap-2"
        data-testid="calculate-btn">
        {busy ? <RefreshCw size={15} className="animate-spin" /> : <ArrowRight size={15} />}
        {busy ? "Calculating..." : isEdit ? "Update & Recalculate" : "Calculate & Continue"}
      </button>

      {/* Version note — shown only when editing existing data */}
      {isEdit && (
        <p className="text-xs text-center text-slate-400">
          Updating this data will create a new calculation version. Your previous version is preserved.
        </p>
      )}

      <p className="text-xs text-center text-slate-400">
        Nigeria emission factors (GHG Protocol 2024). Gas is tracked for intensity only — not added to your footprint.
      </p>
    </div>
  );
}

// ── Step 3: See Your Impact ────────────────────────────────────────────────
function StepImpact({ footprint }) {
  if (!footprint) return null;
  const total = footprint.total_tco2e || 0;
  const cats  = Object.values(footprint.by_category || {})
    .filter(c => c.scope !== "intensity_only" && c.kgco2e > 0)
    .sort((a, b) => b.kgco2e - a.kgco2e);
  const biggest = cats[0];

  // Friendly label map
  const friendly = {
    "Fleet Diesel (Scope 1)":       "Fleet vehicles",
    "Generator Diesel (Scope 1)":   "Generator fuel",
    "Grid Electricity (Scope 2)":   "Electricity",
  };

  return (
    <div className="space-y-4">
      {/* Big number */}
      <div className="text-center py-2">
        <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide mb-1">Your annual carbon footprint</p>
        <p className="text-5xl font-bold text-slate-900">{total}</p>
        <p className="text-base text-slate-400 mt-0.5">tonnes CO₂e per year</p>
      </div>

      {/* Simple breakdown bars */}
      {cats.length > 0 && (
        <div className="space-y-2">
          {cats.map(cat => {
            const pct  = Math.round(cat.kgco2e / (total * 1000) * 100);
            const label = friendly[cat.label] || cat.label;
            return (
              <div key={cat.label}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-slate-600">{label}</span>
                  <span className="font-bold text-slate-900">{Math.round(cat.kgco2e/1000*10)/10}t · {pct}%</span>
                </div>
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-teal-500 transition-all duration-700"
                    style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* The one insight */}
      {biggest && (
        <div className="bg-[#0F172A] rounded-xl px-5 py-4 text-white" data-testid="impact-insight">
          <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide mb-1">Key insight</p>
          <p className="text-sm font-semibold">
            Most of your emissions come from {(friendly[biggest.label] || biggest.label).toLowerCase()} ({Math.round(biggest.kgco2e / (total*1000) * 100)}% of total).
          </p>
          <p className="text-xs text-slate-400 mt-1">This is where you have the most to gain.</p>
        </div>
      )}
    </div>
  );
}

// ── Step 4: Your Opportunities ─────────────────────────────────────────────
function StepOpportunities({ actions }) {
  const IMPACT = { value_chain: "Highest value", transport: "High impact", renewable_energy: "High impact", process: "Good impact" };
  const IMPACT_COLOR = { value_chain: "bg-emerald-100 text-emerald-700", transport: "bg-teal-100 text-teal-700", renewable_energy: "bg-blue-100 text-blue-700", process: "bg-slate-100 text-slate-600" };

  const topActions = (actions?.actions || []).slice(0, 3);
  if (!topActions.length) return null;

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">Based on your data, here are your top opportunities:</p>
      {topActions.map((a, i) => {
        // Clean up descriptions — remove technical parentheses
        const shortDesc = a.description?.split(".")[0].replace(/\([^)]*\)/g, "").trim();
        return (
          <div key={i} className={`rounded-xl border-2 p-4 ${i === 0 ? "border-teal-300 bg-teal-50/30" : "border-slate-200 bg-white"}`}
            data-testid={`opportunity-${i}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <p className="text-sm font-semibold text-slate-900 flex-1">{shortDesc || a.action_type?.replace(/_/g," ")}</p>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${IMPACT_COLOR[a.action_type] || "bg-slate-100 text-slate-500"}`}>
                {IMPACT[a.action_type] || "Impact"}
              </span>
            </div>
            <div className="flex items-center gap-4">
              <div>
                <p className="text-xs text-slate-400">Could reduce by</p>
                <p className="text-lg font-bold text-teal-600">{a.co2_reduction}t <span className="text-xs font-normal text-slate-400">CO₂e/yr</span></p>
              </div>
              {a.annual_savings > 0 && (
                <div>
                  <p className="text-xs text-slate-400">Est. savings</p>
                  <p className="text-lg font-bold text-emerald-600">${a.annual_savings.toLocaleString()} <span className="text-xs font-normal text-slate-400">/yr</span></p>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Step 5: Get Financing ──────────────────────────────────────────────────
const FR_TYPES = [
  { value: "capex_support",       label: "Equipment / Capital Support" },
  { value: "green_loan",          label: "Green Loan" },
  { value: "grant",               label: "Grant / Subsidy" },
  { value: "carbon_project_dev",  label: "Carbon Project Development" },
  { value: "blended_finance",     label: "Blended Finance" },
];

function StepFinancing({ journey }) {
  const [showForm,   setShowForm]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted,  setSubmitted]  = useState(null);
  const [form, setForm] = useState({ request_type: "capex_support", amount: "", purpose: "", timeline: "12" });

  const opp  = journey?.opportunity;
  const fp   = journey?.footprint;

  const handleSubmit = async () => {
    if (!form.purpose.trim()) { toast.warning("Tell us how you'll use the funds."); return; }
    setSubmitting(true);
    try {
      const res = await client.post("/finance-requests/", {
        workspace_type: "sme", assessment_id: journey?.assessment_id,
        request_type: form.request_type,
        amount_requested_usd: form.amount ? parseFloat(form.amount) : null,
        purpose: form.purpose,
        timeline_months: form.timeline ? parseInt(form.timeline) : null,
      });
      await client.post(`/finance-requests/${res.data.request_id}/submit`);
      setSubmitted(res.data.request_id);
      toast.success(`Request submitted — ${res.data.request_id}`);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="text-center py-6" data-testid="financing-submitted">
        <CheckCircle2 size={40} className="text-emerald-500 mx-auto mb-3" />
        <p className="text-xl font-bold text-slate-900">Request Submitted</p>
        <p className="text-sm text-slate-400 mt-1 mb-1">Reference: <strong className="font-mono">{submitted}</strong></p>
        <p className="text-xs text-slate-400 mb-5">A finance partner will review your request and be in touch.</p>
        <div className="flex flex-col gap-2 items-center">
          <button
            onClick={() => window.location.href = "/financing"}
            className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white text-sm font-bold rounded-xl hover:bg-teal-700"
            data-testid="view-requests-btn">
            <ArrowRight size={14} /> View Submitted Request
          </button>
          <button onClick={() => setSubmitted(null)} className="text-xs text-slate-400 underline mt-1">Submit another request</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Value summary — the pitch */}
      {opp && (
        <div className="bg-slate-900 rounded-xl p-5">
          <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-3">What you could unlock</p>
          <div className="grid grid-cols-2 gap-4">
            <MetricCard label="Carbon revenue potential / year" value={`$${(opp.vcm_value_low || 0).toLocaleString()}+`} color="text-teal-400" />
            <MetricCard label="CO₂e you can reduce" value={`${fp?.total_tco2e}t`} color="text-slate-900" />
          </div>
        </div>
      )}

      {!showForm ? (
        <button onClick={() => setShowForm(true)} data-testid="open-financing-btn"
          className="w-full h-12 bg-teal-600 text-white text-sm font-bold rounded-xl hover:bg-teal-700 flex items-center justify-center gap-2">
          <Zap size={15} /> Request Financing
        </button>
      ) : (
        <div className="space-y-3" data-testid="financing-form">
          <select value={form.request_type} onChange={e => setForm(f=>({...f,request_type:e.target.value}))}
            className="w-full h-11 px-4 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none focus:border-teal-400"
            data-testid="financing-type-select">
            {FR_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-4">
            <input type="number" value={form.amount} onChange={e => setForm(f=>({...f,amount:e.target.value}))}
              placeholder="Amount needed (USD)" className="h-11 px-4 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-teal-400"
              data-testid="financing-amount-input" />
            <input type="number" value={form.timeline} onChange={e => setForm(f=>({...f,timeline:e.target.value}))}
              placeholder="Timeline (months)" className="h-11 px-4 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-teal-400"
              data-testid="financing-timeline-input" />
          </div>
          <textarea value={form.purpose} onChange={e => setForm(f=>({...f,purpose:e.target.value}))}
            placeholder="How will you use these funds? (e.g. 'Convert 10 vehicles to CNG and install solar at depot')"
            rows={2} className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm resize-none focus:outline-none focus:border-teal-400"
            data-testid="financing-purpose-input" />
          <button onClick={handleSubmit} disabled={submitting}
            className="w-full h-11 bg-teal-600 text-white text-sm font-bold rounded-xl hover:bg-teal-700 disabled:opacity-40 flex items-center justify-center gap-2"
            data-testid="financing-submit-btn">
            {submitting ? <RefreshCw size={14} className="animate-spin" /> : <ArrowRight size={14} />}
            {submitting ? "Sending your request..." : "Send Request"}
          </button>
          <button onClick={() => setShowForm(false)} className="w-full text-xs text-slate-400 hover:text-slate-600">Cancel</button>
        </div>
      )}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────
export default function SMEJourneyPage() {
  const navigate = useNavigate();
  const { setCurrentStep } = useJourney();
  const [journey,    setJourney]    = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [expanded,   setExpanded]   = useState({});   // completed step expansions

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get("/sme/my-journey");
      setJourney(res.data);
      setCurrentStep(1);
    } catch {
      client.get("/sme/gasavant")
        .then(r => { setJourney(r.data); setLoading(false); })
        .catch(() => setLoading(false));
      return;
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCalculated = (updatedJourney) => {
    setJourney(updatedJourney);
    // Auto-collapse Step 2 after recalculation so user sees the results
    setExpanded(e => ({ ...e, data: false }));
  };

  if (loading) return (
    <div className="flex items-center justify-center h-screen">
      <RefreshCw size={20} className="animate-spin text-teal-500" />
    </div>
  );

  const isEmpty = journey?.state === "not_started";
  const org     = journey?.organization || {};
  const fp      = journey?.footprint;
  const actions = journey?.actions;
  const opp     = journey?.opportunity;

  // Determine step states
  const profileDone = !!org.name;
  const dataDone    = !!fp;
  const impactDone  = !!fp;    // auto-completes with data
  const oppsDone    = !!actions?.action_count;

  const completedSteps = [
    ...(profileDone ? ["profile"] : []),
    ...(dataDone    ? ["data"]    : []),
    ...(impactDone  ? ["impact"]  : []),
    ...(oppsDone    ? ["opportunities"] : []),
  ];

  // Current active step
  const activeStep = !profileDone ? "profile"
    : !dataDone    ? "data"
    : !impactDone  ? "impact"
    : !oppsDone    ? "opportunities"
    :                "financing";

  const stepStatus = (id) => {
    if (completedSteps.includes(id)) return "completed";
    if (id === activeStep) return "active";
    return "locked";
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="sme-journey-page">
      <ProgressHeader currentStep={activeStep} completedSteps={completedSteps} />
      <div className="px-4 pt-4 pb-2">
        <PageHeader
          label="SME WORKSPACE"
          title="Climate Journey"
          subtitle={`${org.name || "Your Organisation"} · ${completedSteps.length} of 5 steps complete`}
        />
      </div>

      <div className="px-4 py-8 space-y-4">
        {/* Step 1 */}
        <StepCard id="profile" number={1} title="Your Business" icon={Building2}
          status={stepStatus("profile")}
          summary={org.name ? `${org.name} · ${org.sector} · ${org.country}` : ""}
          isExpanded={expanded.profile}
          onExpand={() => setExpanded(e => ({...e, profile: !e.profile}))}>
          <StepProfile org={org} />
          <p className="text-xs text-center text-teal-600 mt-3 font-semibold">✓ Profile confirmed — continue to Step 2</p>
        </StepCard>

        {/* Step 2 */}
        <StepCard id="data" number={2} title="Enter Your Data" icon={BarChart2}
          status={stepStatus("data")}
          summary={fp ? `${fp.total_tco2e} tCO₂e/yr · ${Object.values(fp.by_category||{}).filter(c=>c.scope!=="intensity_only").length} source(s)` : ""}
          isExpanded={expanded.data}
          onExpand={() => setExpanded(e => ({...e, data: !e.data}))}
          headerAction={fp ? (
            <button
              onClick={e => { e.stopPropagation(); setExpanded(ex => ({...ex, data: true})); }}
              className="text-[11px] font-semibold text-teal-600 border border-teal-200 rounded-xl px-3 py-1.5 bg-white hover:bg-teal-50 hover:border-teal-400 transition-all"
              data-testid="edit-data-btn">
              Edit Data
            </button>
          ) : null}>
          <StepEnterData
            assessmentId={journey?.assessment_id}
            onCalculated={handleCalculated}
            initialValues={journey?.raw_inputs}
          />
        </StepCard>

        {/* Step 3 */}
        <StepCard id="impact" number={3} title="See Your Impact" icon={BarChart2}
          status={stepStatus("impact")}
          summary={fp ? `${fp.total_tco2e} tCO₂e/yr annual footprint` : ""}
          isExpanded={expanded.impact !== false}
          onExpand={() => setExpanded(e => ({...e, impact: e.impact === false ? true : false}))}>
          <StepImpact footprint={fp} />
        </StepCard>

        {/* Step 4 */}
        <StepCard id="opportunities" number={4} title="Your Opportunities" icon={Target}
          status={stepStatus("opportunities")}
          summary={actions?.action_count ? `${actions.action_count} opportunities identified` : ""}
          isExpanded={expanded.opportunities !== false}
          onExpand={() => setExpanded(e => ({...e, opportunities: e.opportunities === false ? true : false}))}>
          <StepOpportunities actions={actions} />
          {actions?.action_count > 0 && (
            <div className="mt-3 bg-teal-50 border border-teal-200 rounded-xl px-4 py-3">
              <p className="text-xs font-semibold text-teal-700">
                These improvements could generate ${((opp?.vcm_value_low || 0)).toLocaleString()}–${((opp?.vcm_value_high || 0)).toLocaleString()}/year in carbon value.
              </p>
            </div>
          )}
        </StepCard>

        {/* Step 5 */}
        <StepCard id="financing" number={5} title="Get Financing" icon={DollarSign}
          status={stepStatus("financing")}
          summary=""
          isExpanded={true}
          onExpand={() => {}}>
          <StepFinancing journey={journey} />
        </StepCard>

        {/* Footer note */}
        <p className="text-center text-xs text-slate-300 pb-4">
          DeCarbonIQ · Your data is secure and only shared with finance partners you engage.
        </p>
      </div>
    </div>
  );
}
